import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, User, MessageSquare, Calendar, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import emailjs from '@emailjs/browser';
import { useLang } from '../context/LanguageContext';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const CALENDLY_URL = 'https://calendly.com/rhamgffry-cocoonutconcept';

const t = {
  fr: {
    pageTitle: 'Contact | Cocoonut Digital — Studio Créatif Strasbourg',
    metaDesc: 'Contactez Cocoonut Digital pour discuter de votre projet de communication digitale. Restaurants, hôtels, cafés et lieux de vie à Strasbourg.',
    back: 'Retour à l\'accueil',
    label: 'Contact',
    heading1: 'Parlons de',
    heading2: 'votre projet.',
    intro: 'Vous êtes un restaurant, un hôtel, un café ou un lieu de vie à Strasbourg et souhaitez renforcer votre présence digitale ? Parlez-nous de votre vision et imaginons ensemble une communication qui reflète l\'identité et l\'atmosphère de votre établissement.',
    nameLabel: 'Votre nom',
    namePlaceholder: 'Jean Dupont',
    emailLabel: 'Votre email',
    emailPlaceholder: 'jean@example.com',
    messageLabel: 'Votre message',
    messagePlaceholder: 'Parlez-nous de votre projet...',
    note: 'Les accompagnements sont proposés sur devis, selon les besoins et le niveau d\'accompagnement souhaité.',
    submit: 'Envoyer le message',
    submitting: 'Envoi en cours...',
    or: 'ou',
    callPrompt: 'Préférez-vous discuter de vive voix ?',
    callBtn: 'Réserver un appel découverte',
    callDuration: '(30 minutes)',
    loading: 'Chargement...',
    emailDirect: 'Ou écrivez-nous directement à',
    successMsg: 'Message envoyé avec succès ! Nous vous répondrons très bientôt.',
    errorMsg: 'Une erreur est survenue. Veuillez réessayer ou nous contacter directement par email.',
  },
  en: {
    pageTitle: 'Contact | Cocoonut Digital — Creative Studio Strasbourg',
    metaDesc: 'Contact Cocoonut Digital to discuss your digital communication project. Restaurants, hotels, cafés and hospitality venues in Strasbourg.',
    back: 'Back to homepage',
    label: 'Contact',
    heading1: 'Let\'s talk about',
    heading2: 'your project.',
    intro: 'Are you a restaurant, hotel, café or hospitality venue in Strasbourg looking to strengthen your digital presence? Tell us about your vision and let\'s create together a communication that reflects the identity and atmosphere of your establishment.',
    nameLabel: 'Your name',
    namePlaceholder: 'John Smith',
    emailLabel: 'Your email',
    emailPlaceholder: 'john@example.com',
    messageLabel: 'Your message',
    messagePlaceholder: 'Tell us about your project...',
    note: 'All packages are provided on a custom quote basis, depending on your needs and the level of support required.',
    submit: 'Send message',
    submitting: 'Sending...',
    or: 'or',
    callPrompt: 'Prefer to talk in person?',
    callBtn: 'Book a discovery call',
    callDuration: '(30 minutes)',
    loading: 'Loading...',
    emailDirect: 'Or email us directly at',
    successMsg: 'Message sent successfully! We\'ll get back to you very soon.',
    errorMsg: 'An error occurred. Please try again or contact us directly by email.',
  },
};

const ContactPage = () => {
  const { lang, setLang } = useLang();
  const tx = t[lang];
  const formRef = useRef();
  const [formData, setFormData] = useState({ user_name: '', user_email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendlyLoading, setCalendlyLoading] = useState(false);

  const openCalendlyPopup = useCallback(async (e) => {
    e.preventDefault();
    setCalendlyLoading(true);
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
      setCalendlyLoading(false);
      return;
    }
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => {
      if (window.Calendly) window.Calendly.initPopupWidget({ url: CALENDLY_URL });
      setCalendlyLoading(false);
    };
    script.onerror = () => {
      setCalendlyLoading(false);
      window.open(CALENDLY_URL, '_blank');
    };
    document.head.appendChild(script);
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, formRef.current, EMAILJS_PUBLIC_KEY);
      toast.success(tx.successMsg, { duration: 4000 });
      setFormData({ user_name: '', user_email: '', message: '' });
    } catch (error) {
      toast.error(tx.errorMsg, { duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Helmet>
        <title>{tx.pageTitle}</title>
        <meta name="description" content={tx.metaDesc} />
        <link rel="canonical" href={`${window.location.origin}/contact`} />
      </Helmet>

      {/* Header */}
      <div className="bg-[#1a3a52] pt-8 pb-12">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors" data-testid="contact-back-link">
              <ArrowLeft size={18} />
              <span className="text-sm">{tx.back}</span>
            </Link>
            <div className="flex items-center text-sm" data-testid="lang-switcher-contact">
              <button onClick={() => setLang('fr')} className={`transition-colors ${lang === 'fr' ? 'text-[#d4a574] font-semibold' : 'text-white/50 hover:text-white'}`}>FR</button>
              <span className="text-white/30 mx-1.5">|</span>
              <button onClick={() => setLang('en')} className={`transition-colors ${lang === 'en' ? 'text-[#d4a574] font-semibold' : 'text-white/50 hover:text-white'}`}>EN</button>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-2xl mx-auto">
            <span className="text-sm uppercase tracking-widest text-[#d4a574] font-mono block mb-4">{tx.label}</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 leading-tight" data-testid="contact-page-title">
              {tx.heading1}<br /><span className="text-[#d4a574]">{tx.heading2}</span>
            </h1>
            <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-xl mx-auto">{tx.intro}</p>
          </motion.div>
        </div>
      </div>

      {/* Form */}
      <div className="container mx-auto px-6 lg:px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="max-w-2xl mx-auto">
          <div className="bg-[#f5f5f0] rounded-3xl p-8 md:p-12 shadow-lg">
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="user_name" className="block text-sm font-medium text-[#151515] mb-2">{tx.nameLabel}</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#717171]" size={20} />
                  <input type="text" id="user_name" name="user_name" value={formData.user_name} onChange={handleChange} required className="w-full pl-12 pr-4 py-4 bg-white border border-[#151515]/10 rounded-xl focus:outline-none focus:border-[#4a5d6c] transition-colors text-[#151515]" placeholder={tx.namePlaceholder} data-testid="contact-name-input" />
                </div>
              </div>
              <div>
                <label htmlFor="user_email" className="block text-sm font-medium text-[#151515] mb-2">{tx.emailLabel}</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#717171]" size={20} />
                  <input type="email" id="user_email" name="user_email" value={formData.user_email} onChange={handleChange} required className="w-full pl-12 pr-4 py-4 bg-white border border-[#151515]/10 rounded-xl focus:outline-none focus:border-[#4a5d6c] transition-colors text-[#151515]" placeholder={tx.emailPlaceholder} data-testid="contact-email-input" />
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#151515] mb-2">{tx.messageLabel}</label>
                <div className="relative">
                  <MessageSquare className="absolute left-4 top-6 text-[#717171]" size={20} />
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={6} className="w-full pl-12 pr-4 py-4 bg-white border border-[#151515]/10 rounded-xl focus:outline-none focus:border-[#4a5d6c] transition-colors text-[#151515] resize-none" placeholder={tx.messagePlaceholder} data-testid="contact-message-input" />
                </div>
              </div>
              <p className="text-sm text-[#151515]/60 italic text-center">{tx.note}</p>
              <motion.button type="submit" disabled={isSubmitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full bg-[#1a3a52] text-white py-4 px-8 rounded-xl font-medium flex items-center justify-center gap-3 hover:bg-[#4a5d6c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="contact-submit-btn">
                {isSubmitting ? tx.submitting : (<><span>{tx.submit}</span><Send size={20} /></>)}
              </motion.button>
            </form>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#151515]/10"></div></div>
              <div className="relative flex justify-center text-sm"><span className="px-4 bg-[#f5f5f0] text-[#151515]/60">{tx.or}</span></div>
            </div>

            <div className="text-center">
              <p className="text-[#151515]/60 mb-4">{tx.callPrompt}</p>
              <motion.button onClick={openCalendlyPopup} disabled={calendlyLoading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="inline-flex flex-col items-center gap-1 px-8 py-4 bg-white border-2 border-[#d4a574] text-[#1a3a52] rounded-xl hover:bg-[#d4a574]/10 transition-all font-medium disabled:opacity-70 disabled:cursor-wait" data-testid="calendly-popup-btn">
                <span className="flex items-center gap-2">
                  {calendlyLoading ? <div className="animate-spin w-5 h-5 border-2 border-[#d4a574] border-t-transparent rounded-full" /> : <Calendar size={20} className="text-[#d4a574]" />}
                  <span>{calendlyLoading ? tx.loading : tx.callBtn}</span>
                </span>
                <span className="text-xs text-[#717171] font-normal">{tx.callDuration}</span>
              </motion.button>
            </div>
          </div>

          <div className="text-center mt-12 space-y-2">
            <p className="text-[#151515]/60">{tx.emailDirect}</p>
            <a href="mailto:rhamgffry@cocoonutconcept.com" className="text-xl text-[#4a5d6c] hover:text-[#1a3a52] transition-colors font-medium" data-testid="contact-email-link">
              rhamgffry@cocoonutconcept.com
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage;
